import React, { forwardRef, useMemo } from 'react'
import type { Chart as ChartJS, ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import type { Context } from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import { ChartBox } from './ChartBox'
import { COLOURS, FONTS } from './theme'

const PLUGINS = [ChartDataLabels]

export type Segment = {
  name: string
  colour: string
  // Colour of the caption written on the segment.
  text: string
  // Each row's width for this segment, as a share of the row (0-100).
  values: number[]
  // What each row's segment says: "R58", "26".
  captions: string[]
}

type Props = {
  // One label per row; an array is drawn as lines.
  rows: (string | string[])[]
  segments: Segment[]
  rowHeight: number
  label: string
}

// Horizontal bars split into segments, each with its caption written on it.
export const SplitBars = forwardRef<ChartJS<'bar'> | undefined, Props>(({ rows, segments, rowHeight, label }, ref) => {
  const [data, animated, still] = useMemo(() => {
    const widest = Math.max(100, ...rows.map((_, row) => segments.reduce((sum, segment) => sum + segment.values[row], 0)))

    const chartData = {
      labels: rows,
      datasets: segments.map((segment) => ({
        label: segment.name,
        data: segment.values,
        backgroundColor: segment.colour,
        borderColor: COLOURS.white,
        borderWidth: 1.5,
        borderRadius: 4,
        borderSkipped: false,
        maxBarThickness: 40,
        datalabels: {
          display: (context: Context) => Number(context.dataset.data[context.dataIndex]) > 0,
          color: segment.text,
          font: { family: FONTS.heading, size: 14, weight: 700 },
          formatter: (_value: number, context: Context) => segment.captions[context.dataIndex],
        },
      })),
    }

    const options: ChartOptions<'bar'> = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item) => `${item.dataset.label}: ${segments[item.datasetIndex].captions[item.dataIndex]}`,
          },
        },
      },
      scales: {
        x: { stacked: true, display: false, min: 0, max: widest },
        y: {
          stacked: true,
          grid: { display: false, drawBorder: false },
          ticks: { color: COLOURS.ink, font: { family: FONTS.heading, size: 14, weight: '600' } },
        },
      },
    }

    return [chartData, options, { ...options, animation: false as const }]
  }, [rows, segments])

  return (
    <ChartBox height={rows.length * rowHeight} label={label}>
      {(animate) => <Bar ref={ref} data={data} options={animate ? animated : still} plugins={PLUGINS} />}
    </ChartBox>
  )
})
